import React from 'react';
import { ProductTypeEnum } from '@koob/enums';
import TripDayCopyButton from '../TripDayCopyButton';
import TripDayItemRemoveButton from '../TripDayItemRemoveButton';
import TripDayHotelHeader from './TripDayHotelHeader';
import TripDayHotelPriceLine from './TripDayHotelPriceLine';
import TripDayHotelRoomList from './TripDayHotelRoomList';

// The visual body of a loaded hotel day: image, floating controls, header,
// price line and room list. Split out of TripDayHotelCard (US-061) to stay
// under the package's max-lines-per-function cap.
export default function TripDayHotelCardBody({
  dayIndex,
  hotel,
  hotelImage,
  tripFilters,
  isIncludedHotel,
  isFirstInGroup,
  isMinStayUnmet,
  hasBookablePrice,
  isRoomUnavailable,
  hotelPricingInfo,
  totalTravelers,
  isGroupLoading,
  roomsLoading,
  roomsFetching,
  roomsData,
  dayRoomSelection,
  displayedRooms,
  suggestedHotelIds,
  selectedType,
  hasFloatingControls,
  showAccessAlert,
  accessAlertTooltip,
  renderAccessAlert,
  addHotel,
  removeHotel,
  readOnly,
  canRemoveHotel,
  canEditRoomSelection,
  canEditRoomComposition,
  onShowSuggestedHotels,
  onEditRoomSelection,
  onEditRooms,
  t,
}: {
  dayIndex: number;
  hotel: any;
  hotelImage: string;
  tripFilters: any;
  isIncludedHotel: any;
  isFirstInGroup: boolean;
  isMinStayUnmet: boolean;
  hasBookablePrice: boolean;
  isRoomUnavailable: any;
  hotelPricingInfo: any;
  totalTravelers: number;
  isGroupLoading: boolean;
  roomsLoading: boolean;
  roomsFetching: boolean;
  roomsData: any;
  dayRoomSelection: any;
  displayedRooms: any[];
  suggestedHotelIds: any[];
  selectedType: any;
  hasFloatingControls: boolean;
  showAccessAlert: boolean;
  accessAlertTooltip: string;
  renderAccessAlert?: (info: { tooltip: string }) => React.ReactNode;
  addHotel: (hotel: any) => void;
  removeHotel: () => void;
  readOnly: boolean;
  canRemoveHotel: boolean;
  canEditRoomSelection: boolean;
  canEditRoomComposition: boolean;
  onShowSuggestedHotels: () => void;
  onEditRoomSelection: () => void;
  onEditRooms: () => void;
  t: (key: string, options?: Record<string, any>) => string;
}) {
  return (
    <div>
      <img
        src={hotelImage}
        onError={(ev: any) => (ev.target.src = '/img/hotel-bg-placeholder.png')}
        className="absolute inset-0 -z-10 h-full w-full object-cover brightness-150 blur-lg rounded-lg"
      />

      <div className="relative">
        {!isIncludedHotel && canRemoveHotel && (
          <TripDayItemRemoveButton onClick={() => removeHotel()} />
        )}

        {showAccessAlert && renderAccessAlert && (
          <div className="absolute top-5 right-0">
            {renderAccessAlert({ tooltip: accessAlertTooltip })}
          </div>
        )}

        {selectedType === ProductTypeEnum.HOTEL &&
          !isIncludedHotel &&
          !readOnly && (
            <TripDayCopyButton
              tipText={t('tooltip.duplicateHotel')}
              tipPosition={'left'}
              onClick={() => addHotel(hotel)}
            />
          )}

        <TripDayHotelHeader
          hasFloatingControls={hasFloatingControls}
          hotelImage={hotelImage}
          hotel={hotel}
          tripFilters={tripFilters}
          isIncludedHotel={isIncludedHotel}
          suggestedHotelIds={suggestedHotelIds}
          onShowSuggestedHotels={onShowSuggestedHotels}
          t={t}
        />

        <div className="mt-2 pr-10 relative">
          <TripDayHotelPriceLine
            dayIndex={dayIndex}
            isIncludedHotel={isIncludedHotel}
            isGroupLoading={isGroupLoading}
            isMinStayUnmet={isMinStayUnmet}
            hotel={hotel}
            hasBookablePrice={hasBookablePrice}
            isRoomUnavailable={isRoomUnavailable}
            hotelPricingInfo={hotelPricingInfo}
            totalTravelers={totalTravelers}
            t={t}
          />

          {!isIncludedHotel && isFirstInGroup && (
            <TripDayHotelRoomList
              dayIndex={dayIndex}
              roomsLoading={roomsLoading}
              roomsFetching={roomsFetching}
              roomsData={roomsData}
              dayRoomSelection={dayRoomSelection}
              displayedRooms={displayedRooms}
              isRoomUnavailable={isRoomUnavailable}
              canEditRoomSelection={canEditRoomSelection}
              canEditRoomComposition={canEditRoomComposition}
              onEditRoomSelection={onEditRoomSelection}
              onEditRooms={onEditRooms}
              t={t}
            />
          )}
        </div>
      </div>
    </div>
  );
}
